import React, { useState, useContext, useCallback, useRef } from "react";
import { useAuth } from "../hooks/AuthProvider";
import { useNavigate } from "react-router-dom";
import "./ForgotPassword.css";
import { LanguageContext } from "../context/LanguageContext";
import authTranslations from "../languages/auth.json";

import Alert from "../Components/Alert";
import AppDialog from "../Components/AppDialog";

export const ResetPassword = () => {
    const { lang } = useContext(LanguageContext);
    const tRoot = authTranslations[lang] || authTranslations.en;
    const t = tRoot.ResetPassword;

    const isRtl = ["ar", "ur"].includes(lang);

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [passwordError, setPasswordError] = useState("");
    const [confirmPasswordError, setConfirmPasswordError] = useState("");

    const getPasswordChecks = (value = "") => ({
        minLength: value.length >= 8,
        hasLowercase: /[a-z]/.test(value),
        hasUppercase: /[A-Z]/.test(value),
        hasNumber: /\d/.test(value),
        hasSpecial: /[^A-Za-z0-9]/.test(value),
    });

    const passwordChecks = getPasswordChecks(password);
    const passwordPolicyValid = Object.values(passwordChecks).every(Boolean);

    const passwordConditions = [
        { key: "minLength", label: t.conditionMinLength },
        { key: "hasLowercase", label: t.conditionLowercase },
        { key: "hasUppercase", label: t.conditionUppercase },
        { key: "hasNumber", label: t.conditionNumber },
        { key: "hasSpecial", label: t.conditionSpecial },
    ];

    const auth = useAuth();
    const navigate = useNavigate();

    const [alertState, setAlertState] = useState({
        open: false,
        message: "",
        type: "info",
        position: isRtl ? "left" : "right",
    });

    const showAlert = useCallback(
        (type, message) => {
            setAlertState({
                open: true,
                type,
                message,
                position: isRtl ? "left" : "right",
            });
        },
        [isRtl]
    );

    const resolverRef = useRef(null);
    const [dialog, setDialog] = useState({
        open: false,
        type: "alert", // "alert" | "confirm"
        variant: "info", // "info" | "danger" | "error"
        icon: "alert", // "alert" | "delete"
        title: "",
        message: "",
        confirmText: "Ok",
        cancelText: "Cancel",
        showCancel: false,
    });

    const closeDialog = useCallback((result = false) => {
        setDialog((d) => ({ ...d, open: false }));
        if (resolverRef.current) {
            resolverRef.current(result);
            resolverRef.current = null;
        }
    }, []);

    const alert = useCallback(
        (opts) =>
            new Promise((resolve) => {
                resolverRef.current = resolve;

                setDialog({
                    open: true,
                    type: opts?.type || "alert",
                    variant: opts?.variant || "info",
                    icon: opts?.icon || "alert",
                    title: opts?.title || "",
                    message: opts?.message || "",
                    confirmText: opts?.confirmText || t.buttonConfirm,
                    cancelText: opts?.cancelText || "Cancel",
                    showCancel: Boolean(opts?.showCancel),
                });
            }),
        [t.buttonConfirm]
    );

    const onFormSubmit = async (e) => {
        e.preventDefault();
        setPasswordError("");
        setConfirmPasswordError("");
        setSubmitting(true);

        if ("" === password) {
            setPasswordError(t.errorRequiredPassword);
            setSubmitting(false);
            return;
        }
        if (!passwordPolicyValid) {
            setPasswordError(t.errorPasswordPolicy || t.errorPasswordLength);
            setSubmitting(false);
            return;
        }
        if (password !== confirmPassword) {
            setConfirmPasswordError(t.errorPasswordMatch);
            setSubmitting(false);
            return;
        }

        try {
            await auth.resetPasswordAction(password);

            await alert({
                type: "alert",
                variant: "info",
                icon: "alert",
                title: "",
                message: t.successMessage,
                confirmText: t.buttonConfirm,
            });

            navigate("/devices");
        } catch (error) {
            const msg = `${t.failurePrefix} ${error?.message || "Unknown error"}`;

            showAlert("danger", msg);

            await alert({
                type: "alert",
                variant: "danger",
                icon: "alert",
                title: "",
                message: msg,
                confirmText: t.buttonConfirm,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Alert
                open={alertState.open}
                close={() => setAlertState({ ...alertState, open: false })}
                message={alertState.message}
                type={alertState.type}
                position={alertState.position}
            />

            <AppDialog
                open={dialog.open}
                type={dialog.type}
                variant={dialog.variant}
                icon={dialog.icon}
                title={dialog.title}
                message={dialog.message}
                confirmText={dialog.confirmText}
                cancelText={dialog.cancelText}
                showCancel={dialog.showCancel}
                onConfirm={() => closeDialog(true)}
                onCancel={() => closeDialog(false)}
                onClose={() => closeDialog(false)}
            />

            <div className="auth-page-wrapper">
                <div className="auth-center-container">
                    <div className="auth-card-wrapper auth-single-card">
                        <div className="auth-form-container">
                            <div className="auth-header-block">
                                <h2>{t.title}</h2>
                                <p className="auth-subtitle">{t.subtitle}</p>
                            </div>

                            <form onSubmit={onFormSubmit}>
                                <div className="auth-form-group">
                                    <label htmlFor="new-password">{t.placeholderNewPassword}</label>
                                    <div className="auth-input-wrapper">
                                        <input
                                            className="auth-form-input"
                                            value={password}
                                            type="password"
                                            id="new-password"
                                            name="password"
                                            placeholder={t.placeholderNewPassword}
                                            onChange={(ev) => setPassword(ev.target.value)}
                                            required
                                            disabled={submitting}
                                        />
                                    </div>
                                    <div className="auth-password-conditions" aria-live="polite">
                                        {passwordConditions.map((condition) => {
                                            const passed = Boolean(passwordChecks[condition.key]);
                                            return (
                                                <div
                                                    key={condition.key}
                                                    className={`auth-password-condition ${passed ? "pass" : "fail"}`}
                                                >
                                                    <span className="auth-password-condition-dot" aria-hidden="true" />
                                                    <span>{condition.label}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {passwordError ? (
                                        <span className="auth-error-message">{passwordError}</span>
                                    ) : null}
                                </div>

                                <div className="auth-form-group">
                                    <label htmlFor="confirm-password">{t.placeholderConfirmPassword}</label>
                                    <div className="auth-input-wrapper">
                                        <input
                                            className="auth-form-input"
                                            value={confirmPassword}
                                            type="password"
                                            id="confirm-password"
                                            name="confirm-password"
                                            placeholder={t.placeholderConfirmPassword}
                                            onChange={(ev) => setConfirmPassword(ev.target.value)}
                                            required
                                            disabled={submitting}
                                        />
                                    </div>
                                    {confirmPasswordError ? (
                                        <span className="auth-error-message">{confirmPasswordError}</span>
                                    ) : null}
                                </div>

                                <button className="auth-submit-btn" type="submit" disabled={submitting}>
                                    {submitting ? t.buttonResetting : t.buttonReset}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
